import { addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTION_META, caregiverName } from './activityUtils';
import { classifySleepType } from './wakeWindowUtils';

function padTime(d) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * logTimerEntry — directly writes a completed timer session to Firestore.
 * Called by ActiveTimerBanner so the user never has to touch a form.
 *
 * @param {object} timerResult  { type, startTime: ISO, elapsedSeconds }
 * @param {string} childId
 * @param {object} user         Firebase Auth user
 * @param {object} profile      Optional user profile { displayName, role }
 */
export async function logTimerEntry(timerResult, childId, user, profile = null) {
  const { type, startTime, elapsedSeconds, wakeUps = [] } = timerResult;
  const loggedAt   = new Date().toISOString();
  const startDate  = new Date(startTime);
  const endDate    = new Date(startDate.getTime() + elapsedSeconds * 1000);

  let collectionName, entry;

  if (type === 'sleep') {
    collectionName = 'sleepTracking';
    const startHHMM   = padTime(startDate);
    const endHHMM     = padTime(endDate);
    const durationMin = Math.round(elapsedSeconds / 60);
    entry = {
      childId,
      userId:     user.uid,
      date:       startTime,
      startTime:  startHHMM,
      endTime:    endHHMM,
      type:       classifySleepType(startHHMM, durationMin),
      notes:      '',
      wakeUps,
      wakeUpCount: wakeUps.length,
      createdAt:  loggedAt,
      loggedBy: {
        uid:  user.uid,
        name: profile?.displayName || caregiverName(user),
        role: profile?.role || '',
      },
    };
  } else if (type === 'breastfeeding') {
    collectionName = 'breastfeeding';
    entry = {
      childId,
      userId:    user.uid,
      date:      startTime,
      side:      'left',
      duration:  Math.max(1, Math.round(elapsedSeconds / 60)),
      notes:     '',
      createdAt: loggedAt,
      loggedBy: {
        uid:  user.uid,
        name: profile?.displayName || caregiverName(user),
        role: profile?.role || '',
      },
    };
  } else {
    throw new Error(`Unsupported timer type: ${type}`);
  }

  // Write the main tracking entry
  await addDoc(collection(db, collectionName), entry);

  // Write the activity feed entry (mirrors useFirestore.addItem)
  const loggedBy = {
    uid:   user.uid,
    name:  profile?.displayName || caregiverName(user),
    role:  profile?.role  || '',
    email: user.email || '',
  };

  const meta = COLLECTION_META[collectionName];
  if (meta) {
    await addDoc(collection(db, 'activityFeed'), {
      childId,
      collectionName,
      icon:      meta.icon,
      label:     meta.label,
      summary:   meta.getSummary(entry),
      eventTime: startTime,
      loggedAt,
      loggedBy,
    });
  }

  // Auto-log any feeds flagged during sleep wake-ups (bottle / breastfeeding)
  if (type === 'sleep' && wakeUps.length > 0) {
    for (const wakeUp of wakeUps) {
      if (!wakeUp.feedLogged) continue;

      if (wakeUp.cause === 'bottle' && wakeUp.bottleAmount) {
        const feedEntry = {
          childId,
          userId:    user.uid,
          date:      wakeUp.time,
          amount:    wakeUp.bottleAmount,
          notes:     'Logged during sleep wake-up',
          createdAt: loggedAt,
        };
        await addDoc(collection(db, 'bottleFeeds'), feedEntry);
        const bottleMeta = COLLECTION_META.bottleFeeds;
        await addDoc(collection(db, 'activityFeed'), {
          childId,
          collectionName: 'bottleFeeds',
          icon:      bottleMeta.icon,
          label:     bottleMeta.label,
          summary:   bottleMeta.getSummary(feedEntry),
          eventTime: wakeUp.time,
          loggedAt,
          loggedBy,
        });
      }

      if (wakeUp.cause === 'breastfeeding' && wakeUp.bfDuration) {
        // const wakeDate  = new Date(wakeUp.time);
        const feedEntry = {
          childId,
          userId:    user.uid,
          date:      wakeUp.time,
          side:      'left',
          duration:  wakeUp.bfDuration,
          notes:     'Logged during sleep wake-up',
          createdAt: loggedAt,
        };
        await addDoc(collection(db, 'breastfeeding'), feedEntry);
        const bfMeta = COLLECTION_META.breastfeeding;
        await addDoc(collection(db, 'activityFeed'), {
          childId,
          collectionName: 'breastfeeding',
          icon:      bfMeta.icon,
          label:     bfMeta.label,
          summary:   bfMeta.getSummary(feedEntry),
          eventTime: wakeUp.time,
          loggedAt,
          loggedBy,
        });
      }
    }
  }
}
